import { useEffect, useState } from "react";
import DallasHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Dallas?", a: "We offer Dallas-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit a Dallas office?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (a Dallas post office or county clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your Dallas address." },
  { q: "Where can I get a passport acceptance in Dallas?", a: "Dallas has acceptance facilities at many post offices (downtown, Uptown, Oak Lawn, Lakewood, Preston Hollow) as well as Collin County and Dallas County clerk offices. We maintain an updated list of Dallas-area facilities with hours, photo availability, and appointment options." },
  { q: "Does the Dallas Passport Agency handle walk-ins?", a: "The Dallas Passport Agency (located in downtown Dallas) requires appointments for life-or-death emergency travel only. Walk-ins are rarely accepted. For travel within 3–14 days, our registered courier service is the fastest legally available channel without agency restrictions." },
  { q: "What is the best price for expedited passport service in Dallas?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
  { q: "What documents do Dallas applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. Child passport: DS-11, birth certificate, both parents or notarized consent, and photos. We provide a Dallas-specific checklist." },
  { q: "Can I get a passport photo in Dallas?", a: "Yes — many Dallas post offices, CVS, Walgreens, UPS Stores, and AAA offices offer passport photos. We recommend confirming photo standards compliance before visiting. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
  { q: "How do I check my Dallas passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Dallas to final return shipping typically matches your chosen speed tier." },
  { q: "Is Dallas near a passport agency?", a: "The Dallas Passport Agency is located at 300 Commerce St, Suite 100, Dallas, TX 75242. However, it only serves travelers with life-or-death emergencies. For standard expedited needs — even urgent ones — our courier channel is faster and more accessible than agency appointments." },
  { q: "Can you help with passports for DFW International Airport travel?", a: "Yes. DFW is one of the world's busiest international airports, and we regularly assist Dallas travelers with expedited passports for international departures. We understand the DFW corridor — from Arlington to Plano to Irving — and can coordinate timelines around your departure." },
];

function DallasStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function DallasProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Dallas Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Dallas travelers benefit from our full speed tier menu. From DFW executives to Frisco families, we match your timeline to your departure.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Dallas travelers departing within 3 days. Full document review and overnight courier submission to State Department." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For DFW-area business travelers and families with travel in 1–2 weeks. Best value for most Dallas clients." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For North Texas residents planning ahead. Significantly faster than USPS standard processing (10–13 weeks)." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DallasServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Dallas travelers with imminent DFW departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Dallas residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Dallas adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 guidance for Dallas and Plano residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for DFW families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport for frequent Dallas business travelers with overlapping trips." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before State Department submission." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Dallas Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Complete passport solutions for Dallas-Fort Worth metro residents — from corporate executives to university students to military families.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DallasLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Dallas</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Dallas is the third-largest city in Texas and the heart of the Dallas-Fort Worth Metroplex — a sprawling 12-county region of 7.5 million people. The DFW corridor is home to American Airlines headquarters, AT&T headquarters, ExxonMobil, Southwest Airlines, and dozens of Fortune 500 companies whose employees travel internationally on short notice, creating some of the highest demand for expedited passport services in the southern United States.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Dallas-Fort Worth International Airport (DFW) is the world's fourth-busiest airport by passenger traffic and offers more than 230 international destinations. DFW's massive footprint — spanning both Dallas and Fort Worth — means millions of North Texas residents travel internationally each year through this single mega-hub. American Airlines' global network creates countless scenarios where a passport renewal missed by a few weeks can cascade into a cancelled international itinerary.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The DFW area has experienced explosive growth in recent decades, drawing transplants from across the United States and internationally. Frisco, Prosper, McKinney, and Allen — the fastest-growing suburbs in North Texas — are full of young families booking their first international vacations. Many of these families have children who have never had passports, or parents whose passports expired during the COVID years and were never renewed.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Dallas has a significant international population including large Korean, Indian, Chinese, Vietnamese, Mexican, and Central American communities. Many Dallas residents maintain close ties to family abroad — traveling frequently for weddings, funerals, holidays, and extended family visits. Urgent passport needs in these communities often arise with one to two weeks notice, sometimes less.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            The Dallas-area tech corridor — stretching from downtown through the Galleria area to Plano and Allen — generates steady corporate travel demand. Tech companies like Texas Instruments, Fidelity, and numerous VC-backed startups send engineers and business development staff abroad on short notice. A deal closing in Singapore or a partnership meeting in London can emerge with 7–10 days of runway, requiring passport processing that standard government channels simply cannot accommodate.
          </p>
        </div>
      </div>
    </section>
  );
}

function DallasTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Dallas Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DFW — Dallas-Fort Worth International Airport</h3>
                <p className="text-gray-400 text-xs">World's 4th busiest airport, American Airlines primary hub</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">DFW handles more than 73 million passengers annually with direct flights to London, Tokyo, Cancun, Paris, Frankfurt, and 230+ destinations. American Airlines' massive hub operation means DFW connects North Texas to virtually every international market. The airport's global reach creates regular passport urgencies — executives booking last-minute international trips, families with expired passports, and students heading abroad for study programs.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> American Airlines' international award travel often gets booked months in advance — then passengers realize their passport expires during the trip. Check validity the moment you book, not when you pack.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DAL — Love Field (Southwest Airlines)</h3>
                <p className="text-gray-400 text-xs">Dallas Love Field, domestic + limited international service</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Love Field serves Dallas intown neighborhoods — Uptown, Oak Lawn, Highland Park — with Southwest flights. Many Dallas residents choose Love Field for domestic connections to international gateways like Miami, Houston, or Atlanta. The convenience of Love Field sometimes creates a false sense of travel flexibility — a missed connection at IAH due to a passport issue can cascade quickly.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> If you are connecting through another U.S. hub to fly internationally, you still need a valid passport at your point of departure in Dallas.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-2-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Corporate Travel &amp; Fortune 500 Demand</h3>
                <p className="text-gray-400 text-xs">American Airlines, AT&amp;T, ExxonMobil, Southwest Airlines, Toyota</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Dallas-Fort Worth hosts more Fortune 500 companies per capita than almost any metropolitan area in the United States. American Airlines alone moves thousands of employees internationally each year. When corporate travel plans surface on short timelines — quarterly board meetings in London, annual conferences in Tokyo, global training programs in Germany — passport processing urgencies emerge that standard post office services cannot handle.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Dallas corporate travelers often book through company travel desks that do not flag passport expiration. Always self-check your passport validity, especially for any trip to a country requiring 6 months beyond return date.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">University Study Abroad &amp; Student Travel</h3>
                <p className="text-gray-400 text-xs">UT Dallas, SMU, TCU, UNT, UTA, Dallas colleges</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">The DFW metroplex has one of the largest concentrations of university students in the country. SMU, UT Dallas, TCU, UNT, UTA, and Richland College collectively send thousands of students abroad each year for semester exchange, summer programs, and spring break immersions. Many of these students — especially first-generation college students — have never had passports. The acceptance-to-orientation pipeline is often 6–10 weeks, requiring expedited processing to meet program start dates.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants in Dallas must visit an acceptance facility in person. We guide students through the nearest facility with the earliest available appointment, so they do not miss their program orientation.</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-pin-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Dallas &amp; Cross-Border Mexico Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">Dallas has a large and growing Hispanic population with strong family ties to Mexico. Weekend trips to Monterrey, holiday visits to family hometowns, and medical tourism to Mexican border cities create a constant stream of passport urgencies — especially for extended family members who have never traveled internationally before.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function DallasDestinationsSection() {
  const destinations = [
    { name: "Mexico", context: "Family heritage trips, Cancun and Riviera Maya resorts, Monterrey business, medical dental tourism", urgency: "Spring break bookings peak January through March", icon: "ri-map-pin-2-line" },
    { name: "United Kingdom", context: "American Airlines direct flights to London Heathrow, corporate meetings, heritage travel", urgency: "Business trips booked 2–3 weeks out with fixed meeting dates", icon: "ri-map-pin-2-line" },
    { name: "Japan & South Korea", context: "American Airlines DFW-Tokyo direct flight, Samsung and LG supplier meetings, tech industry travel", urgency: "Tech deal travel often has 1–2 week booking-to-departure windows", icon: "ri-map-pin-2-line" },
    { name: "Germany", context: "DFW-Frankfurt direct route, auto industry suppliers, Texas Manufacturing Assistance Center connections", urgency: "Industry conference and trade show dates are fixed and unmovable", icon: "ri-map-pin-2-line" },
    { name: "Caribbean & Bahamas", context: "Galveston and Miami cruise departures, family vacation packages, spring break resort bookings", urgency: "Last-minute cruise deals and all-inclusive packages booked 3–4 weeks out", icon: "ri-map-pin-2-line" },
    { name: "Canada", context: "Dallas-to-Toronto business travel, Niagara Falls family trips, Vancouver tech industry connections", urgency: "Cross-border business bookings often 1–2 weeks out", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "42%", label: "of Dallas expedited requests are corporate or business-related with under 2 weeks notice" },
    { stat: "2–4 Weeks", label: "average lead time Dallas travelers have from trip booking to international departure" },
    { stat: "Jan–Mar", label: "peak season when spring break, corporate Q1 travel, and study abroad deadlines collide" },
    { stat: "36 Hours", label: "shortest turnaround we have accommodated for a DFW executive departing to London" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Dallas Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across the Lone Star State with the same fast, reliable passport processing.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/houston" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Houston <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/austin" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Austin <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/fort-worth" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Fort Worth <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DallasTestimonialsSection() {
  const testimonials = [
    { name: "Kevin A.", location: "Uptown Dallas", text: "My company booked me to London in 9 days and my passport was expired. Fast Passport Center got it back in 48 hours. The document review was thorough and they caught an issue I never would have noticed.", rating: 5 },
    { name: "The Kim Family", location: "Plano", text: "We booked a last-minute trip to Seoul and realized our daughter's passport expired two months ago. They walked us through everything and the passport arrived in 5 days. Incredible service.", rating: 5 },
    { name: "Maria G.", location: "Irving", text: "I needed a name change on my passport before a conference in Madrid. They handled the entire process remotely — I never had to visit an office. Smooth and fast.", rating: 5 },
    { name: "David L.", location: "Frisco", text: "Third time using Fast Passport Center for DFW-to-Japan business trips. They know exactly what to do and the timeline is always accurate. Won't use anyone else.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Dallas Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DallasAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Dallas Main Post Office", address: "400 N Ervay St, Dallas, TX 75201", phone: "(214) 760-4536", photo: "Yes", appointment: "Required" },
    { name: "Oak Lawn Station Post Office", address: "3730 Cedar Springs Rd, Dallas, TX 75219", phone: "(214) 521-6001", photo: "Yes", appointment: "Walk-in" },
    { name: "Lakewood Branch Post Office", address: "2345 Abrams Rd, Dallas, TX 75214", phone: "(214) 821-4747", photo: "Yes", appointment: "Required" },
    { name: "Collin County Clerk Office", address: "2300 Bloomdale Rd, McKinney, TX 75071", phone: "(972) 548-4185", photo: "No", appointment: "Required" },
    { name: "Plano Post Office", address: "1900 E Spring Creek Pkwy, Plano, TX 75074", phone: "(972) 423-9701", photo: "Yes", appointment: "Required" },
    { name: "Irving Post Office", address: "120 Main St, Irving, TX 75061", phone: "(972) 721-2407", photo: "Yes", appointment: "Walk-in" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Dallas Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility. Here are Dallas-area locations with contact information and appointment requirements.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Call ahead to confirm availability. We can help find the earliest available slot across all Dallas-area facilities.</p>
        </div>
      </div>
    </section>
  );
}

function DallasComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Dallas Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies" },
                { feature: "Dallas-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function DallasMistakesSection() {
  const mistakes = [
    { title: "Assuming DFW Agency Takes Walk-Ins", desc: "The Dallas Passport Agency requires appointments even for emergencies. Many Dallas residents arrive without an appointment and are turned away. Our courier channel bypasses this entirely." },
    { title: "Using DS-82 When DS-11 Is Required", desc: "Dallas applicants often attempt the simpler renewal form when their passport was issued at 15 or younger. A rejected form costs 1–2 weeks. We review before you submit." },
    { title: "Photo Standards Violations", desc: "Many Dallas pharmacies and big-box stores produce photos that fail State Department standards. We catch this in review before it causes a rejection." },
    { title: "Missing Parental Consent for Minors", desc: "North Texas families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly." },
    { title: "Not Accounting for 6-Month Rule", desc: "Dallas travelers booking to Japan, Southeast Asia, or Europe often do not know their passport must have 6 months remaining beyond the return date. We flag this immediately." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Dallas Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DallasHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Dallas-area acceptance facility. We find the nearest one with the earliest appointment." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed package overnight to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. Full tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Delivered to your Dallas address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Dallas Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DallasFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Dallas Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DallasFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Dallas Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are departing DFW in 3 days or booking a cruise from Galveston in 3 weeks, we will get your passport processed on time. A+ BBB rated. Registered State Dept. courier.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function DallasPage() {
  useEffect(() => {
    document.title = "Emergency Passport Dallas, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/dallas/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Dallas, TX — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Dallas business travelers, DFW airport passengers, university students, and North Texas families.");
    setMeta("geo.placename", "Dallas, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "32.7767;-96.7970");
    setMeta("ICBM", "32.7767, -96.7970");
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <DallasHeroSection />
      <DallasStickyNav />
      <DallasProcessingTimeSection />
      <DallasServicesSection />
      <DallasLocalAreaSection />
      <DallasTravelTimingSection />
      <DallasDestinationsSection />
      <DallasTestimonialsSection />
      <DallasAcceptanceFacilitiesSection />
      <DallasComparisonSection />
      <DallasMistakesSection />
      <DallasHowItWorksSection />
      <DallasFAQSection />
      <SEOCrossLinks currentRoute="/cities/dallas" state="TX" />
      <DallasFinalCTA />
    </div>
  );
}